import { useMutation, useQuery } from "@apollo/client";
import { useFormik } from "formik";
import { useEffect } from "react";
import * as Yup from "yup";

import { GridInputs } from "../../../../../../../components/Functional";
import { Button } from "../../../../../../../components/ui";
import {
  CREATE_CRM_REGISTRAR as createCRMRegistrarMutation,
  UPDATE_CRM_REGISTRAR as updateCRMRegistrarMutation,
} from "../../../../../../../graphql/mutation/CRMRegistrar";
import { FETCH_CRM_TYPES } from "../../../../../../../graphql/query/CRMType";
import { useEntities } from "../../../../../../../hooks/useEntities";
import { currentLocaleISODate } from "../../../../../../../util";

const AddTechnical = props => {
  const { instanceId, closeModal, values, isOpen } = props;
  const { entities } = useEntities();

  const checkDisabled = {
    disabled: values?.id ? true : false,
    inputClassName: values?.id
      ? "!bg-white !text-gray-400 !border-gray-400"
      : "",
  };

  const { data: { crmTypes = [] } = {} } = useQuery(FETCH_CRM_TYPES, {
    variables: { input: { categoryId: 3 } },
  });

  const [createCRMRegistrar, { loading: loadingCreate }] = useMutation(
    createCRMRegistrarMutation(),
    { refetchQueries: ["FetchCRMRegistrarsV2"] },
  );

  const [updateCRMRegistrar, { loading: loadingUpdate }] = useMutation(
    updateCRMRegistrarMutation(),
    { refetchQueries: ["FetchCRMRegistrarsV2"] },
  );

  const addTechnical = () => {
    const input = {
      categoryId: 3, // id of 'CRMCategory: {description: Technical}'
      description: formik.values.description,
      issueId: formik.values.issueId || null,
      typeId: formik.values.typeId,
    };

    if (values?.id) {
      input.id = values?.id;
    } else {
      input.date = formik.values.date;
      input.userId = formik.values.userId;
      input.entityId = input.userId;
      input.instanceId = instanceId;
    }

    const action = values?.id ? updateCRMRegistrar : createCRMRegistrar;

    action({
      variables: {
        input,
      },
    }).then(() => {
      closeModal();
      formik.resetForm();
    });
  };

  const formik = useFormik({
    initialValues: values
      ? {
          date: values.date,
          description: values.description,
          issueId: values.issueId,
          typeId: values.typeId,
          userId: values.userId,
        }
      : {
          date: currentLocaleISODate(),
        },
    enableReinitialize: true,
    validateOnChange: true,
    validateOnMount: true,
    validationSchema: Yup.object({
      date: Yup.date().required("Please enter a date."),
      userId: Yup.number().required("Please select user."),
    }),
    onSubmit: addTechnical,
  });

  const inputs = {
    className: "grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-8 lg:gap-12",
    inputs: [
      {
        className: "grid grid-cols-1 gap-4",
        inputs: [
          { label: "Date", name: "date", type: "date", ...checkDisabled },
          {
            label: "Type",
            name: "typeId",
            type: "multi-select",
            placeholder: "Select a type",
            options: crmTypes?.map(item => ({
              label: item?.description,
              value: item?.id,
            })),
          },
          {
            label: "Description",
            name: "description",
            rows: 4,
            type: "textarea",
          },
        ],
      },
      {
        className: "grid grid-cols-1 gap-4",
        inputs: [
          { label: "Issue", name: "issueId", type: "number" },
          {
            label: "User Name",
            name: "userId",
            type: "multi-select",
            placeholder: "Select user",
            ...checkDisabled,
            options: entities?.edges?.map(item => ({
              label: item?.name,
              value: item?.id,
            })),
          },
        ],
      },
    ],
  };

  const inputProps = {
    ...inputs,
    formik,
  };

  const successLabel = values?.id ? "Edit" : "Add";

  useEffect(() => {
    formik.handleReset();
  }, [isOpen]);

  return (
    <div className="flex w-full flex-col p-4 sm:p-6 lg:p-8">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start">
        <div className="flex flex-1 flex-col text-2xl font-bold">
          {values?.id ? "Edit" : "Add"} Technical Record
        </div>
        <div className="flex flex-wrap gap-2 sm:gap-4">
          <div className="flex flex-wrap gap-2 sm:gap-4">
            <Button
              action="default"
              className="sm:mr-4"
              label="Cancel"
              onClick={closeModal}
            />
            <Button
              label={`${successLabel}${
                loadingUpdate || loadingCreate ? "ing" : ""
              } Technical`}
              disabled={loadingUpdate || loadingCreate}
              onClick={formik.submitForm}
            />
          </div>
        </div>
      </div>
      <div className="mt-12 pb-10">
        <GridInputs {...inputProps} />
      </div>
    </div>
  );
};

export default AddTechnical;
